import Seo from "@/components/Seo";
import { PageHero, CTABand, Reveal } from "@/components/ui-bits";
import { useCertificates, useDocuments } from "@/hooks/useContent";
import { track } from "@/lib/site";
import { ShieldCheck, FileText, Award, FileDown } from "lucide-react";

const SECTIONS = [
  { title: "Supplier Selection", text: "We work towards selecting suitable suppliers for each product requirement, discussing product capability, consistency and reliability before orders are confirmed." },
  { title: "Product Specifications", text: "Specifications such as grade, size, form and packaging are agreed with the buyer in writing before order confirmation, so expectations are clear on both sides." },
  { title: "Quality Checks", text: "Product preparation is supported with quality checks as applicable to the product and the agreed specification." },
  { title: "Packaging Checks", text: "Packaging is reviewed against the agreed requirement — format, labelling and suitability for international transit." },
  { title: "Documentation", text: "Export documentation is coordinated as applicable to the order and destination market." },
  { title: "Traceability", text: "Where applicable, sourcing details for the supplied product can be shared with the buyer as part of the order documentation." },
  { title: "Shipment Coordination", text: "From order confirmation to shipment, our team coordinates timelines, documentation and logistics as applicable to the order." },
];

export default function Quality() {
  const { data: certificates } = useCertificates();
  const { data: documents } = useDocuments();
  const visible = (certificates || []).filter((c) => c.published);

  return (
    <>
      <Seo
        title="Quality & Compliance — Orvella Overseas"
        description="Our quality approach covers supplier selection, agreed product specifications, quality and packaging checks, export documentation and shipment coordination."
        path="/quality"
      />

      <PageHero
        eyebrow="Quality & Compliance"
        title="A Quality Approach Built on Clarity"
        sub="Clear specifications, careful sourcing and coordinated documentation — so international buyers know exactly what they are receiving."
      />

      <section className="section-pad">
        <div className="container-x grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.04}>
              <div className="card card-hover h-full p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-sand text-gold-dark">
                  <ShieldCheck size={19} strokeWidth={1.8} />
                </span>
                <h2 className="mt-4 font-bold text-navy">{s.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-mist">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section-pad bg-sand" data-testid="certificates-section">
        <div className="container-x">
          <Reveal>
            <span className="eyebrow">Certificates</span>
            <span className="mt-3 block h-[2px] w-12 bg-gold" />
            <h2 className="mt-5 font-serif text-3xl font-semibold text-navy">Certificates & Compliance Documents</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-mist">
              Certifications and compliance documents are displayed here as they are confirmed and issued to the company.
            </p>
          </Reveal>
          {visible.length === 0 ? (
            <Reveal delay={0.1}>
              <div className="card mt-8 flex items-start gap-4 p-6" data-testid="certificates-empty-state">
                <Award size={22} className="mt-0.5 shrink-0 text-gold-dark" />
                <p className="text-sm leading-relaxed text-mist">
                  Certificate details are being compiled and will be published once confirmed. [TO BE UPDATED]
                  <br />
                  If your requirement needs specific certificates or test documents, please mention it in your quote request and
                  our export team will confirm applicability.
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {visible.map((c, i) => (
                <Reveal key={c.id} delay={i * 0.05}>
                  <div className="card h-full p-6" data-testid={`certificate-card-${c.id}`}>
                    <FileText size={20} className="text-gold-dark" />
                    <h3 className="mt-3 font-bold text-navy">{c.name}</h3>
                    <dl className="mt-3 space-y-1.5 text-sm text-mist">
                      {c.number && <div><dt className="inline font-bold text-navy">No. </dt><dd className="inline">{c.number}</dd></div>}
                      {c.authority && <div><dt className="inline font-bold text-navy">Authority: </dt><dd className="inline">{c.authority}</dd></div>}
                      {c.issue_date && <div><dt className="inline font-bold text-navy">Issued: </dt><dd className="inline">{c.issue_date}</dd></div>}
                      {c.expiry_date && <div><dt className="inline font-bold text-navy">Valid till: </dt><dd className="inline">{c.expiry_date}</dd></div>}
                    </dl>
                    {c.pdf_url && (
                      <a href={c.pdf_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-xs font-bold uppercase tracking-wider text-gold-dark underline decoration-gold/50 underline-offset-4">
                        View Certificate PDF
                      </a>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section-pad" data-testid="documents-section">
        <div className="container-x">
          <Reveal>
            <span className="eyebrow">Company Documents</span>
            <span className="mt-3 block h-[2px] w-12 bg-gold" />
            <h2 className="mt-5 font-serif text-3xl font-semibold text-navy">Published Company Documents</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-mist">
              Official company documents available for download. Documents appear here as the company publishes them.
            </p>
          </Reveal>
          {(documents || []).length === 0 ? (
            <Reveal delay={0.08}>
              <div className="card mt-8 flex items-start gap-4 p-6" data-testid="documents-empty-state">
                <FileText size={22} className="mt-0.5 shrink-0 text-gold-dark" />
                <p className="text-sm leading-relaxed text-mist">
                  Company documents are being prepared and will be published here shortly. [TO BE UPDATED]
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {documents.map((d, i) => (
                <Reveal key={d.id} delay={(i % 2) * 0.05}>
                  <div className="card card-hover flex h-full items-center gap-4 p-5" data-testid={`document-card-${d.id}`}>
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-navy text-gold-soft">
                      <FileText size={22} strokeWidth={1.8} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-navy">{d.title}</h3>
                      {d.description && <p className="mt-0.5 text-xs leading-relaxed text-mist">{d.description}</p>}
                    </div>
                    <a
                      href={d.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => track("document_download", { title: d.title })}
                      data-testid={`document-download-${d.id}`}
                      className="btn-gold shrink-0 !px-4 !py-2 !text-[11px]"
                    >
                      <FileDown size={14} /> Open
                    </a>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTABand />
    </>
  );
}
